import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, TrendingUp, Users, Heart, Check, ChevronDown } from "lucide-react";
import Hummingbird from "../components/brand/Hummingbird";
import SEO from "../components/SEO";

const MentorPage = () => {
  const [referrals, setReferrals] = useState(10);
  const [avgOrder, setAvgOrder] = useState(290);
  const [rate, setRate] = useState(0.20);

  const monthly = referrals * avgOrder * rate;
  const yearly = monthly * 12;

  const tiers = [
    { name: "Bronze", min: "0 Verkäufe", rate: 20, perks: ["Eigener Empfehlungs-Link", "Mentor-Toolkit", "Forum-Zugang"] },
    { name: "Silber", min: "10 Verkäufe / Quartal", rate: 25, perks: ["+5% Provision", "Co-Branding-Material", "Monatliches Mentor-Call"], featured: true },
    { name: "Gold", min: "30 Verkäufe / Quartal", rate: 30, perks: ["+10% Provision", "Persönliches Mentor-Profil auf SAT", "Speaker-Slots bei SAT-Events"] },
  ];

  return (
    <div className="pt-28 min-h-screen" data-testid="mentor-page">
      <SEO
        title="SAT Mentor · Ausbildung zum Begleiter"
        description="Werde SAT Mentor. Die Ausbildung für Menschen, die andere professionell begleiten, coachen und transformativ wirken möchten — von Nenad Vladović."
        path="/mentor"
      />
      {/* Hero */}
      <section className="relative pt-12 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="/brand/nenad-vest.jpg" alt="Nenad — SAT Founder" className="w-full h-full object-cover object-[center_20%] opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#081328] via-[#081328]/70 to-[#081328]/10"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#081328]"></div>
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="overline mb-4">SAT · Mentor-Programm</div>
          <h1 className="headline-caps text-[10vw] sm:text-[7vw] lg:text-7xl xl:text-8xl text-[#F7F6F2]" style={{ fontWeight: 600 }}>
            <span className="block">WERDE</span>
            <span className="block headline-italic" style={{ textTransform: "none", fontStyle: "italic", fontWeight: 500 }}>SAT Mentor.</span>
          </h1>
          <p className="serif italic text-2xl text-[#F2D6D6]/75 mt-8 max-w-2xl">„Ein SAT Mentor ist kein Retter und kein Guru. Ein SAT Mentor ist ein Code-Leser, Raumhalter und Integrationsbegleiter."</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/erstgespraech" className="btn-primary pulse-gold" data-testid="mentor-cta-erstgespraech">Eignungs- und Erstgespräch anfragen <ArrowRight size={16} /></Link>
            <Link to="/sat-htc" className="btn-secondary" data-testid="mentor-htc-cta">SAT-HTC Methode kennenlernen <ArrowRight size={16} /></Link>
          </div>
        </div>
        <div className="absolute right-6 top-32 hidden md:block opacity-50 hummingbird-float pointer-events-none">
          <Hummingbird size={170} />
        </div>
      </section>

      {/* SAT-HTC Anbindung */}
      <section className="py-20 bg-[#081328]" data-testid="mentor-htc-context">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Was ein SAT Mentor wirklich ist</div>
            <h2 className="serif text-3xl md:text-5xl font-light leading-tight">
              Kein Retter. Kein Guru.
              <span className="block italic text-[#D4AF37] mt-2">Ein Code-Leser.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-white/75 text-lg leading-relaxed">
            <p>
              Ein SAT Mentor ist Code-Leser, Raumhalter, Spiegel und Integrationsbegleiter.
              In der SAT MENTOR Ausbildung lernst du, Menschen über
              {" "}<Link to="/sat-htc" className="text-[#D4AF37] hover:underline">SAT-HTC™</Link>{" "}
              zu begleiten: durch Wesensanalyse, Herkunftscode, Schutzmuster,
              Schattenarbeit, Körperwahrnehmung, Beziehungscode, Glaubenscode,
              Recode-Arbeit und gelebte Integration.
            </p>
            <p className="serif italic text-white/85">
              Du lernst, tiefe Prozesse verantwortungsvoll zu halten — mit Klarheit, Würde,
              Ethik und Herz.
            </p>
            <Link
              to="/sat-htc"
              className="inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:translate-x-1 transition mt-2"
              data-testid="mentor-htc-link"
            >
              Die SAT-HTC Methode kennenlernen <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="py-20 bg-[#050B1B]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="overline mb-3">Warum SAT Mentor?</div>
          <h2 className="headline-xl text-4xl md:text-6xl">Drei Gründe, warum Menschen Mentor werden.</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: Heart, t: "Sinn", d: "Du hilfst Menschen, die Spielregeln des Lebens zu verstehen — und veränderst damit Familien, Beziehungen, Gesellschaften." },
              { icon: TrendingUp, t: "Sinnvolle Vergütung", d: "Für SAT-Mentoren gibt es klare, faire Konditionen. Details werden im Erstgespräch transparent besprochen." },
              { icon: Users, t: "Gemeinschaft", d: "Du wirst Teil eines wachsenden Mentor-Netzwerks mit monatlichen Calls und Live-Events." },
            ].map((b, i) => (
              <div key={i} className="glass rounded-2xl p-8" data-testid={`mentor-benefit-${i}`}>
                <b.icon size={20} className="text-[#D4AF37] mb-4" />
                <div className="serif text-2xl mb-2">{b.t}</div>
                <p className="text-white/65 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Referrals-Programm (Kalkulator/Tiers/So funktionierts) — für MVL öffentlich ausgeblendet.
          Bleibt im Code erhalten, ist über /referrals nach Login weiterhin erreichbar. */}
      {false && (<>
      <section id="calculator" className="py-24 bg-[#081328]">
        <div className="container-sat max-w-[1100px]">
          <div className="overline mb-3">Verdienst-Rechner</div>
          <h2 className="headline-xl text-4xl md:text-6xl">Was kannst du verdienen?</h2>

          <div className="grid lg:grid-cols-12 gap-10 mt-12 items-start">
            <div className="lg:col-span-7 glass rounded-3xl p-8 space-y-6" data-testid="mentor-calculator">
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm text-white/75">Empfehlungen pro Monat</label>
                  <span className="text-[#D4AF37] font-semibold">{referrals}</span>
                </div>
                <input type="range" min="1" max="100" value={referrals} onChange={(e) => setReferrals(+e.target.value)} className="w-full accent-[#D4AF37]" data-testid="calc-referrals" />
                <div className="flex justify-between text-xs text-white/40 mt-1"><span>1</span><span>100</span></div>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm text-white/75">Ø Bestellwert (CHF)</label>
                  <span className="text-[#D4AF37] font-semibold">{avgOrder}</span>
                </div>
                <input type="range" min="49" max="1490" step="10" value={avgOrder} onChange={(e) => setAvgOrder(+e.target.value)} className="w-full accent-[#D4AF37]" data-testid="calc-avg-order" />
                <div className="flex justify-between text-xs text-white/40 mt-1"><span>CHF 49</span><span>CHF 1'490</span></div>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm text-white/75">Dein Tier (Provision)</label>
                  <span className="text-[#D4AF37] font-semibold">{(rate * 100).toFixed(0)}%</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[0.20, 0.25, 0.30].map((r) => (
                    <button
                      key={r}
                      onClick={() => setRate(r)}
                      className={`py-3 rounded-lg border text-sm transition ${rate === r ? "bg-[#D4AF37] text-[#081328] border-[#D4AF37]" : "border-white/10 text-white/65 hover:bg-white/[0.04]"}`}
                      data-testid={`calc-tier-${(r * 100).toFixed(0)}`}
                    >
                      {(r * 100).toFixed(0)}%
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 glass rounded-3xl p-8 text-center" data-testid="mentor-calculator-result">
              <div className="overline mb-3">Dein Potenzial</div>
              <div className="text-xs uppercase tracking-widest text-white/45 mb-1">Pro Monat</div>
              <div className="serif text-6xl font-light text-[#D4AF37]">CHF {monthly.toLocaleString("de-CH", { maximumFractionDigits: 0 })}</div>
              <div className="text-xs uppercase tracking-widest text-white/45 mt-8 mb-1">Pro Jahr</div>
              <div className="serif text-4xl font-light text-white/90">CHF {yearly.toLocaleString("de-CH", { maximumFractionDigits: 0 })}</div>
              <p className="text-white/40 text-xs mt-6">Beispielrechnung · keine Garantien. Echte Verkäufe und Ergebnisse hängen von deinem Engagement ab.</p>
              <Link to="/auth/register?redirect=/referrals" className="btn-primary w-full justify-center mt-8" data-testid="calc-cta">Mentor werden <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-24 bg-[#050B1B]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="overline mb-3">Tier-System</div>
          <h2 className="headline-xl text-4xl md:text-6xl">Je mehr du gibst, desto mehr bekommst du zurück.</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {tiers.map((tier) => (
              <div key={tier.name} className={`relative rounded-3xl p-8 border ${tier.featured ? "bg-gradient-to-b from-[#D4AF37]/8 to-[#4B2E83]/8 border-[#D4AF37]/40" : "glass"}`} data-testid={`tier-${tier.name.toLowerCase()}`}>
                {tier.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#D4AF37] text-[#081328] text-[10px] uppercase tracking-widest font-semibold">Empfohlen</div>}
                <div className="overline mb-3">{tier.name}</div>
                <div className="serif text-6xl font-light text-[#D4AF37]">{tier.rate}<span className="text-2xl">%</span></div>
                <div className="text-sm text-white/55 mt-2">{tier.min}</div>
                <ul className="mt-6 space-y-2">
                  {tier.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-white/80"><Check size={14} className="text-[#D4AF37] mt-1 shrink-0" /> {p}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How */}
      <section className="py-24 bg-[#081328]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <div className="overline mb-3">So funktioniert es</div>
          <h2 className="headline-xl text-4xl md:text-6xl">In 3 Schritten startklar.</h2>
          <div className="mt-10 space-y-6">
            {[
              { n: "01", t: "Konto anlegen", d: "Registriere dich kostenlos auf SAT. Du erhältst sofort deinen persönlichen Empfehlungs-Link." },
              { n: "02", t: "Teilen", d: "WhatsApp, Mail, Social Media, Visitenkarte — überall, wo du Menschen erreichst." },
              { n: "03", t: "Verdienen", d: "Sobald jemand über deinen Link kauft, wird die Provision automatisch deinem Konto gutgeschrieben." },
            ].map((s) => (
              <div key={s.n} className="flex gap-6 items-start">
                <div className="num-mark serif italic text-4xl shrink-0 w-16">{s.n}</div>
                <div>
                  <div className="serif text-2xl">{s.t}</div>
                  <p className="text-white/65 mt-2">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 flex justify-center">
            <Link to="/erstgespraech" className="btn-primary pulse-gold" data-testid="mentor-bottom-cta">Eignungs- und Erstgespräch anfragen <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>
      </>)}

      {/* MVL-fokussierte Aufnahme-Sektion */}
      <section className="py-28 bg-[#050B1B]" data-testid="mentor-aufnahme-cta">
        <div className="container-sat max-w-3xl text-center">
          <div className="overline mb-4">Aufnahme SAT MENTOR</div>
          <h2 className="serif text-4xl md:text-5xl font-light leading-tight">
            Nicht jeder ist für den Mentor-Weg bereit.
          </h2>
          <p className="text-white/70 mt-8 text-lg leading-relaxed">
            Die SAT MENTOR Ausbildung ist begleitet, verbindlich und tief. Bevor wir gemeinsam starten,
            treffen wir uns in einem persönlichen Eignungs- und Erstgespräch. Dort klären wir Reife,
            Voraussetzungen, Umfang, Ablauf und Investition — offen, respektvoll, ohne Verkaufsdruck.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/erstgespraech" className="btn-primary" data-testid="mentor-final-cta">
              Eignungsgespräch anfragen <ArrowRight size={16} />
            </Link>
            <Link to="/sat-htc" className="btn-secondary" data-testid="mentor-final-htc">
              SAT-HTC Methode kennenlernen
            </Link>
          </div>
          <p className="text-white/45 text-xs italic mt-8">
            Aufnahme und Investition werden im Erstgespräch transparent geklärt.
          </p>
        </div>
      </section>
    </div>
  );
};

export default MentorPage;
